import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import isHotkey from "is-hotkey";
import escapeHtml from "escape-html";
import { jsx } from "slate-hyperscript";
import IconBold from "~/components/Icons/icon-bold";
import IconItalic from "~/components/Icons/icon-italic";
import IconUnderlined from "~/components/Icons/icon-underlined";
import IconList from "~/components/Icons/icon-list";

import s from "./RichTextExample.less";

import { Editable, withReact, useSlate, Slate, ReactEditor } from "slate-react";

import { withHistory, HistoryEditor } from "slate-history";

// export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor

import { Button } from "./components";
// import ElementSlate from 'slate'
import {
  Text,
  createEditor,
  Node,
  Transforms,
  Element as SlateElement,
  Editor,
  Descendant,
  BaseEditor,
} from "slate";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

// const { Node: NodeSrcubs } = ElementSlate

export type BlockQuoteElement = {
  type: "block-quote";
  align?: string;
  children: Descendant[];
};

export type BulletedListElement = {
  type: "bulleted-list";
  align?: string;
  children: Descendant[];
};

export type CheckListItemElement = {
  type: "check-list-item";
  checked: boolean;
  children: Descendant[];
};

export type EditableVoidElement = {
  type: "editable-void";
  children: EmptyText[];
};

export type HeadingElement = {
  type: "heading";
  align?: string;
  children: Descendant[];
};

export type HeadingTwoElement = {
  type: "heading-two";
  align?: string;
  children: Descendant[];
};

export type ImageElement = {
  type: "image";
  url: string;
  children: EmptyText[];
};

export type LinkElement = { type: "link"; url: string; children: Descendant[] };

export type ButtonElement = { type: "button"; children: Descendant[] };

export type ListItemElement = { type: "list-item"; children: Descendant[] };

export type MentionElement = {
  type: "mention";
  character: string;
  children: CustomText[];
};

export type ParagraphElement = {
  type: "paragraph";
  align?: string;
  children: Descendant[];
};

export type TitleElement = { type: "title"; children: Descendant[] };

export type VideoElement = {
  type: "video";
  url: string;
  children: EmptyText[];
};

type CustomElement =
  | BlockQuoteElement
  | BulletedListElement
  | CheckListItemElement
  | EditableVoidElement
  | HeadingElement
  | HeadingTwoElement
  | ImageElement
  | LinkElement
  | ButtonElement
  | ListItemElement
  | MentionElement
  | ParagraphElement
  | TitleElement
  | VideoElement;

export type CustomText = {
  bold?: boolean;
  italic?: boolean;
  code?: boolean;
  text: string;
};

export type EmptyText = {
  text: string;
};

export type CustomEditor = BaseEditor & ReactEditor & HistoryEditor;

declare module "slate" {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
    Text: CustomText | EmptyText;
  }
}

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

const LIST_TYPES = ["numbered-list", "bulleted-list"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];

interface IProps {
  textCommand?: string;
  isEdit?: boolean;
  setTextCommand?;
}

const editorer = {
  children: [
    {
      type: "paragraph",
      children: [
        { text: "An opening paragraph with a " },
        {
          type: "link",
          url: "https://example.com",
          children: [{ text: "link" }],
        },
        { text: " in it." },
      ],
    },
    {
      type: "quote",
      children: [{ text: "A wise quote." }],
    },
    {
      type: "paragraph",
      children: [{ text: "A closing paragraph!" }],
    },
  ],
  // `Editor` objects also have other properties that are omitted here...
};

const serialize = (node) => {
  if (Text.isText(node)) {
    let string = escapeHtml(node.text);
    //@ts-ignore
    if (node.bold) {
      string = `<strong>${string}</strong>`;
    }
    //@ts-ignore
    if (node.code) {
      string = `<code>${string}</code>`;
    }
    //@ts-ignore
    if (node.italic) {
      string = `<em>${string}</em>`;
    }
    //@ts-ignore
    if (node.underline) {
      string = `<u>${string}</u>`;
    }
    return string;
  }

  const children = node.children.map((n) => serialize(n)).join("");

  switch (node.type) {
    case "quote":
      return `<blockquote><p>${children}</p></blockquote>`;
    case "paragraph":
      return `<p>${children}</p>`;
    case "bulleted-list":
      return ` <ul style="list-style-type: disc;">${children}</ul>`;
    case "list-item":
      return `<li>${children}</li>`;
    case "link":
      return `<a href="${escapeHtml(node.url)}">${children}</a>`;
    default:
      return children;
  }
};

const deserialize = (el, markAttributes = {}) => {
  if (el.nodeType === 3) {
    return jsx("text", markAttributes, el.textContent);
  } else if (el.nodeType !== 1) {
    return null;
  }

  const nodeAttributes = { ...markAttributes };

  // define attributes for text nodes
  switch (el.nodeName) {
    case "STRONG":
      nodeAttributes["bold"] = true;
      break;
    case "EM":
      nodeAttributes["italic"] = true;
      break;
    case "U":
      nodeAttributes["underline"] = true;
      break;
  }
  function flatDeep(arr, d = 1) {
    return d > 0
      ? arr.reduce(
          (acc, val) =>
            acc.concat(Array.isArray(val) ? flatDeep(val, d - 1) : val),
          []
        )
      : arr.slice();
  }
  const children = flatDeep(
    Array.from(el.childNodes).map((node) => deserialize(node, nodeAttributes))
  );

  if (children.length === 0) {
    children.push(jsx("text", nodeAttributes, ""));
  }

  switch (el.nodeName) {
    case "BODY":
      return jsx("fragment", {}, children);
    case "BR":
      return "\n";
    case "BLOCKQUOTE":
      return jsx("element", { type: "quote" }, children);
    case "P":
      return jsx("element", { type: "paragraph" }, children);
    case "UL":
      return jsx("element", { type: "bulleted-list" }, children);
    case "LI":
      return jsx("element", { type: "list-item" }, children);
    case "A":
      return jsx(
        "element",
        { type: "link", url: el.getAttribute("href") },
        children
      );
    default:
      return children;
  }
};

const RichTextExample: FC<IProps> = ({
  textCommand,
  isEdit,
  setTextCommand,
}) => {
  const { t } = useTranslation();
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(
    () => withHistory(withReact(createEditor() as ReactEditor)),
    []
  );

  const document = new DOMParser().parseFromString(textCommand, "text/html");
  // deserialize(document.body)
  const [editorHtml, setEditorHtml] = useState([]);

  const floff = {};

  floff["children"] = editorHtml;

  useEffect(() => {
    if (editorHtml.length > 0) {
      setTextCommand(serialize(floff));
    }
  }, [editorHtml]);

  return (
    <>
      <Slate
        editor={editor}
        value={deserialize(document.body)}
        onChange={(value) => {
          setEditorHtml(value);
        }}
      >
        <div className={s.editorBox}>
          <div className={s.toolbar}>
            <MarkButton format="bold" icon="format_bold" />
            <MarkButton format="italic" icon="format_italic" />
            <MarkButton format="underline" icon="format_underlined" />
            {/*<MarkButton format="code" icon="code" />*/}
            {/*<BlockButton format="heading-one" icon="looks_one" />*/}
            {/*<BlockButton format="heading-two" icon="looks_two" />*/}
            {/*<BlockButton format="block-quote" icon="format_quote" />*/}
            {/*<BlockButton format="numbered-list" icon="format_list_numbered" />*/}
            <BlockButton format="bulleted-list" icon="format_list_bulleted" />
            {/*<BlockButton format="left" icon="format_align_left" />*/}
            {/*<BlockButton format="center" icon="format_align_center" />*/}
            {/*<BlockButton format="right" icon="format_align_right" />*/}
            {/*<BlockButton format="justify" icon="format_align_justify" />*/}
          </div>
          <Editable
            className={s.editable}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={t("announcement.announcement_text")}
            spellCheck
            autoFocus
            onKeyDown={(event) => {
              for (const hotkey in HOTKEYS) {
                if (isHotkey(hotkey, event as any)) {
                  event.preventDefault();
                  const mark = HOTKEYS[hotkey];
                  toggleMark(editor, mark);
                }
              }
            }}
          />
        </div>
      </Slate>
    </>
  );
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? "align" : "type"
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  let newProperties: Partial<SlateElement>;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? "paragraph" : isList ? "list-item" : format,
    };
  }
  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  );

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      );
    case "bulleted-list":
      return (
        <ul style={{ listStyleType: "disc" }} {...attributes}>
          {children}
        </ul>
      );
    case "heading-one":
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case "heading-two":
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case "list-item":
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const ElementIcon = (icon) => {
  switch (icon) {
    case "format_bold":
      return <IconBold />;
    case "format_italic":
      return <IconItalic />;
    case "format_underlined":
      return (
        <span className={s.IconUnderlined}>
          <IconUnderlined />
        </span>
      );
    case "format_list_bulleted":
      return <IconList />;
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.code) {
    children = <code>{children}</code>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

const BlockButton = ({ format, icon }) => {
  const editor = useSlate();
  const active = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? "align" : "type"
  );

  return (
    <Button
      active={active}
      className={clsx(s.toolbarButton, {
        [s.toolbarButtonActive]: active,
      })}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <span
        className={clsx(s.notActive, {
          [s.isActiveMark]: active,
        })}
      >
        {ElementIcon(icon)}
      </span>
    </Button>
  );
};

const MarkButton = ({ format, icon }) => {
  const editor = useSlate();
  const active = isMarkActive(editor, format);

  return (
    <Button
      active={active}
      className={clsx(s.toolbarButton, {
        [s.toolbarButtonActive]: active,
      })}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <span
        className={clsx(s.notActive, {
          [s.isActiveMark]: active,
        })}
      >
        {ElementIcon(icon)}
      </span>
    </Button>
  );
};

const initialValue: Descendant[] = [
  {
    type: "paragraph",
    children: [
      { text: "This is editable " },
      { text: "rich", bold: true },
      { text: " text, " },
      { text: "much", italic: true },
      { text: " better than a " },
      { text: "!" },
    ],
  },
  {
    type: "paragraph",
    children: [
      {
        text: "Since it's rich text, you can do things like turn a selection of text ",
      },
      {
        text: "bold",
        // bold: true
      },
      {
        text: ", or add a semantically rendered block quote in the middle of the page, like this:",
      },
    ],
  },
];

export default RichTextExample;
